import type { QiValue } from './types';

/**
 * I-4 (Paul, 27 Oct): an INDICATIVE Star Ratings Quality Measures band from a home's own ledger
 * values, before lock. Never a predicted rating.
 *
 * Method: DHDA Star Ratings Provider Manual 2.11 §2.4 (docs/regulatory-sources/
 * doh-star-ratings-provider-manual-v2.11.pdf, SHA256 691445de…). Five equally weighted
 * indicators; categories weighted (Table 6); each category scored by national quintile 1–5;
 * indicator score = weighted sum / sum of weights; total 5–25 mapped to stars (Table 7).
 *
 * Limits, all shown to the user:
 *  - Quintile cut-points and the risk-adjustment regression coefficients are not published (the
 *    method, formula and variables are: Manual 2.11 §2.4.2–2.4.3, Table 8). The cut-points
 *    below are derived from the public quarterly extract and the home's values are NOT risk
 *    adjusted (the Manual adjusts pressure injuries, weight loss and falls).
 *  - The extract publishes pressure injuries as one combined rate, so the stage weighting
 *    cannot be reproduced; the stage 2+ rate is bounded instead.
 *  - Reproducing published ratings from the extract alone matched 68.5% of 2,530 homes exactly
 *    and 98.7% within one star (lane 7d, 27 Oct 2026), so a one-star band is always shown.
 */
export const QI_STAR_QM_SOURCE = {
  manual: 'Star Ratings Provider Manual 2.11, section 2.4',
  extract:
    'Star Ratings quarterly data extract, October 2026 (QI Program data April to June 2026; GPMS extract 13 October 2026)',
  extractSha256: 'c68733f90d55231ba7a2ee7a3d2cb2baab40d49e4460f5ac0028b99ee684563a',
  derivedOn: '2026-10-27',
  exactMatchPercent: 68.5,
  withinOneStarPercent: 98.7,
} as const;

/** 20th/40th/60th/80th percentile of each published value (whole percent), 2,530 homes. */
export const QI_STAR_QM_CUTPOINTS = {
  pressureInjuries: [0, 0, 1, 1],
  restrictivePractices: [6, 14, 21, 32],
  weightLoss: [2, 4, 6, 8],
  falls: [26, 30, 34, 37],
  majorInjury: [0, 1, 2, 3],
  polypharmacy: [28, 33, 38, 43],
  antipsychotics: [3, 7, 10, 15],
} as const satisfies Record<string, readonly [number, number, number, number]>;
type Category = keyof typeof QI_STAR_QM_CUTPOINTS;

export interface QiStarIndicator {
  key: 'PI' | 'RP' | 'UPWL' | 'FMI' | 'MM';
  /** Category rates in percent; [low, high] when the rate is bounded rather than known. */
  rates: { category: Category; low: number; high: number }[];
  scoreLow: number;
  scoreHigh: number;
}
export type QiStarBand =
  | { status: 'unavailable'; missing: QiStarIndicator['key'][] }
  | {
      status: 'indicative';
      indicators: QiStarIndicator[];
      totalLow: number;
      totalHigh: number;
      /** Central estimate from the midpoint of the total. */
      stars: number;
      /** Always at least one star either side (feasibility accuracy). */
      starsLow: number;
      starsHigh: number;
    };

const n = (values: Readonly<Record<string, QiValue | undefined>>, code: string): number | null => {
  const v = values[code];
  return v?.state === 'measured' && typeof v.value === 'number' && Number.isFinite(v.value)
    ? v.value
    : null;
};
const quintile = (category: Category, ratePercent: number): number =>
  1 + QI_STAR_QM_CUTPOINTS[category].filter((cut) => Math.round(ratePercent) > cut).length;
export const qiStarsForTotal = (total: number): number =>
  total < 10 ? 5 : total < 12 ? 4 : total < 16 ? 3 : total < 18 ? 2 : 1;

export function qiStarQualityBand(
  values: Readonly<Record<string, QiValue | undefined>>,
): QiStarBand {
  const pct = (num: number | null, den: number | null) =>
    num === null || den === null || den <= 0 ? null : (100 * Math.max(0, num)) / den;
  const missing: QiStarIndicator['key'][] = [];
  const indicators: QiStarIndicator[] = [];
  const add = (
    key: QiStarIndicator['key'],
    rates: ({ category: Category; low: number | null; high: number | null } | null)[],
  ) => {
    if (rates.some((r) => !r || r.low === null || r.high === null)) {
      missing.push(key);
      return;
    }
    const known = rates as { category: Category; low: number; high: number }[];
    const avg = (pick: 'low' | 'high') =>
      known.reduce((s, r) => s + quintile(r.category, r[pick]), 0) / known.length;
    indicators.push({ key, rates: known, scoreLow: avg('low'), scoreHigh: avg('high') });
  };

  const assessed = n(values, 'PI-01');
  const stages = ['PI-06', 'PI-07', 'PI-08', 'PI-09', 'PI-10'].map((c) => n(values, c));
  const any = n(values, 'PI-04');
  add('PI', [
    stages.some((s) => s === null)
      ? null
      : {
          category: 'pressureInjuries',
          // People with a stage 2+ injury: at least the largest stage count, at most the
          // smaller of the stage sum and everyone with any injury.
          low: pct(Math.max(...(stages as number[])), assessed),
          high: pct(
            Math.min(
              (stages as number[]).reduce((a, b) => a + b, 0),
              any ?? Number.POSITIVE_INFINITY,
            ),
            assessed,
          ),
        },
  ]);
  const one = (category: Category, num: string, den: string) => {
    const r = pct(n(values, num), n(values, den));
    return { category, low: r, high: r };
  };
  add('RP', [one('restrictivePractices', 'PR-04', 'PR-02')]);
  add('UPWL', [one('weightLoss', 'UPWL-05', 'UPWL-01')]);
  add('FMI', [one('falls', 'FMI-03', 'FMI-01'), one('majorInjury', 'FMI-04', 'FMI-01')]);
  const received = n(values, 'MM-10');
  const psychosis = n(values, 'MM-11');
  const withoutPsychosis = received === null || psychosis === null ? null : received - psychosis;
  const antipsychotics = pct(withoutPsychosis, n(values, 'MM-08'));
  add('MM', [
    one('polypharmacy', 'MM-04', 'MM-02'),
    { category: 'antipsychotics', low: antipsychotics, high: antipsychotics },
  ]);

  // Manual §2.4.1: missing data for any indicator means no Quality Measures rating at all.
  if (missing.length) return { status: 'unavailable', missing };
  const totalLow = indicators.reduce((s, i) => s + Math.min(i.scoreLow, i.scoreHigh), 0);
  const totalHigh = indicators.reduce((s, i) => s + Math.max(i.scoreLow, i.scoreHigh), 0);
  // eslint-disable-next-line statura/no-floating-point-money -- Quality indicator scores are dimensionless, not monetary amounts.
  const stars = qiStarsForTotal((totalLow + totalHigh) / 2);
  return {
    status: 'indicative',
    indicators,
    totalLow,
    totalHigh,
    stars,
    starsLow: Math.max(1, Math.min(qiStarsForTotal(totalHigh), stars) - 1),
    starsHigh: Math.min(5, Math.max(qiStarsForTotal(totalLow), stars) + 1),
  };
}
